import { splitName } from '~shared/lib/airtable';
import { normEmail, normName } from '~shared/lib/normalize';
import type {
  LookupResult,
  MatchKind,
  MemberHistory,
  MemberSnapshot,
  Overlap,
  QueryToken,
  SnapTeam,
  TeamRole,
} from './types';

export const MAX_MATCHES = 10;

export type HistoryIndex = {
  snapshot: MemberSnapshot;
  byId: Map<string, MemberHistory>;
  byFullName: Map<string, string[]>;
  byWord: Map<string, string[]>;
  byEmail: Map<string, string[]>;
};

const push = (m: Map<string, string[]>, k: string, v: string): void => {
  const cur = m.get(k);
  if (cur) cur.push(v);
  else m.set(k, [v]);
};

const byStartDesc = (a: string | null, b: string | null): number =>
  (b ?? '').localeCompare(a ?? '');

export function buildHistoryIndex(snapshot: MemberSnapshot): HistoryIndex {
  const byId = new Map<string, MemberHistory>();
  for (const member of snapshot.members) {
    byId.set(member.id, { member, teams: [], clinics: [] });
  }
  addTeams(snapshot, byId);
  addClinics(snapshot, byId);
  return { snapshot, byId, ...buildLookups(byId) };
}

function roleOf(team: SnapTeam, id: string): TeamRole {
  if (team.captainId === id) return 'Captain';
  if (team.coCaptainId === id) return 'Co-Captain';
  return 'Player';
}

function addTeams(
  snapshot: MemberSnapshot,
  byId: Map<string, MemberHistory>,
): void {
  const seasons = new Map(snapshot.seasons.map((s) => [s.id, s]));
  for (const team of snapshot.teams) {
    const season = team.seasonId ? seasons.get(team.seasonId) : undefined;
    const ids = new Set(team.rosterIds);
    if (team.captainId) ids.add(team.captainId);
    if (team.coCaptainId) ids.add(team.coCaptainId);
    for (const id of ids) {
      const entry = byId.get(id);
      if (!entry) continue;
      entry.teams.push({
        teamId: team.id,
        teamName: team.name,
        flavour: team.flavour,
        seasonName: season?.name ?? 'Unknown season',
        seasonStart: season?.startDate ?? null,
        role: roleOf(team, id),
      });
    }
  }
}

function addClinics(
  snapshot: MemberSnapshot,
  byId: Map<string, MemberHistory>,
): void {
  const terms = new Map(snapshot.terms.map((t) => [t.id, t]));
  for (const clinic of snapshot.clinics) {
    const term = clinic.termId ? terms.get(clinic.termId) : undefined;
    for (const id of new Set(clinic.participantIds)) {
      const entry = byId.get(id);
      if (!entry) continue;
      entry.clinics.push({
        clinicId: clinic.id,
        clinicName: clinic.name,
        termName: term?.name ?? 'Unknown term',
        termStart: term?.startDate ?? null,
        level: clinic.level,
        day: clinic.day,
        location: clinic.location,
      });
    }
  }
}

function buildLookups(
  byId: Map<string, MemberHistory>,
): Pick<HistoryIndex, 'byFullName' | 'byWord' | 'byEmail'> {
  const byFullName = new Map<string, string[]>();
  const byWord = new Map<string, string[]>();
  const byEmail = new Map<string, string[]>();
  for (const entry of byId.values()) {
    entry.teams.sort((a, b) => byStartDesc(a.seasonStart, b.seasonStart));
    entry.clinics.sort((a, b) => byStartDesc(a.termStart, b.termStart));

    const email = normEmail(entry.member.email);
    if (email) push(byEmail, email, entry.member.id);

    const key = normName(entry.member.name);
    if (!key) continue;
    push(byFullName, key, entry.member.id);
    const { first, last } = splitName(entry.member.name);
    for (const word of new Set([normName(first), normName(last)])) {
      if (word) push(byWord, word, entry.member.id);
    }
  }
  return { byFullName, byWord, byEmail };
}

const weight = (h: MemberHistory): number =>
  (h.member.duplicateOf ? 0 : 1000) + h.teams.length + h.clinics.length;

const rank = (index: HistoryIndex, ids: string[]): MemberHistory[] =>
  ids
    .map((id) => index.byId.get(id))
    .filter((h): h is MemberHistory => h != null)
    .sort(
      (a, b) =>
        weight(b) - weight(a) || a.member.name.localeCompare(b.member.name),
    );

const FUZZY_LIMIT = 5;
const FUZZY_MIN_SCORE = 0.75;

function editDistance(a: string, b: string): number {
  const prev: number[] = [];
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    let diag = prev[0]!;
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const tmp = prev[j]!;
      prev[j] = Math.min(
        prev[j]! + 1,
        prev[j - 1]! + 1,
        diag + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      diag = tmp;
    }
  }
  return prev[b.length]!;
}

function wordScore(q: string, w: string): number {
  if (q === w) return 1;
  if (q.length >= 3 && (w.startsWith(q) || q.startsWith(w))) return 0.9;
  const max = Math.max(q.length, w.length);
  if (max < 4) return 0;
  const d = editDistance(q, w);
  return d <= 2 ? 1 - d / max : 0;
}

function fuzzyMatches(index: HistoryIndex, key: string): MemberHistory[] {
  if (key.length < 3) return [];
  const queryWords = key.split(' ');
  const scored: { entry: MemberHistory; score: number }[] = [];
  for (const entry of index.byId.values()) {
    const nameWords = normName(entry.member.name).split(' ').filter(Boolean);
    if (nameWords.length === 0) continue;
    let total = 0;
    for (const q of queryWords) {
      let best = 0;
      for (const w of nameWords) best = Math.max(best, wordScore(q, w));
      total += best;
    }
    const score = total / queryWords.length;
    if (score >= FUZZY_MIN_SCORE) scored.push({ entry, score });
  }
  return scored
    .sort(
      (a, b) =>
        b.score - a.score ||
        weight(b.entry) - weight(a.entry) ||
        a.entry.member.name.localeCompare(b.entry.member.name),
    )
    .slice(0, FUZZY_LIMIT)
    .map((s) => s.entry);
}

const toResult = (
  token: QueryToken,
  kind: MatchKind,
  all: MemberHistory[],
): LookupResult => ({
  token,
  kind,
  matches: all.slice(0, MAX_MATCHES),
  truncated: Math.max(0, all.length - MAX_MATCHES),
});

export function lookup(index: HistoryIndex, token: QueryToken): LookupResult {
  if (token.name.includes('@')) {
    const all = rank(index, index.byEmail.get(normEmail(token.name)) ?? []);
    return toResult(token, all.length ? 'exact' : 'none', all);
  }

  const key = normName(token.name);
  const isSingleWord = !key.includes(' ');
  const ids =
    index.byFullName.get(key) ??
    (isSingleWord ? (index.byWord.get(key) ?? []) : []);
  const all = rank(index, ids);
  if (all.length) {
    return toResult(
      token,
      index.byFullName.has(key) ? 'exact' : 'single-word',
      all,
    );
  }

  const fuzzy = fuzzyMatches(index, key);
  return toResult(token, fuzzy.length ? 'fuzzy' : 'none', fuzzy);
}

export const matchedNonMember = (r: LookupResult): boolean =>
  (r.kind === 'exact' || r.kind === 'single-word') &&
  r.matches.every((h) => !h.member.ftloMember && !h.member.bobaMember);

export function resultPriority(r: LookupResult): number {
  if (matchedNonMember(r)) return 0;
  if (r.kind === 'none' || r.kind === 'fuzzy') return 1;
  return 2;
}

export function findOverlaps(results: LookupResult[]): Overlap[] {
  const primaries = results
    .map((r) => r.matches.at(0))
    .filter((h): h is MemberHistory => h != null);
  if (primaries.length < 2) return [];

  const teams = new Map<string, { label: string; names: string[] }>();
  const clinics = new Map<string, { label: string; names: string[] }>();

  for (const h of primaries) {
    for (const t of h.teams) {
      const e = teams.get(t.teamId) ?? {
        label: `${t.seasonName} · ${t.teamName}`,
        names: [],
      };
      e.names.push(h.member.name);
      teams.set(t.teamId, e);
    }
    for (const c of h.clinics) {
      const e = clinics.get(c.clinicId) ?? { label: c.clinicName, names: [] };
      e.names.push(h.member.name);
      clinics.set(c.clinicId, e);
    }
  }

  const out: Overlap[] = [];
  for (const [, e] of teams) {
    if (e.names.length > 1)
      out.push({ kind: 'team', label: e.label, memberNames: e.names });
  }
  for (const [, e] of clinics) {
    if (e.names.length > 1)
      out.push({ kind: 'clinic', label: e.label, memberNames: e.names });
  }
  return out.sort((a, b) => b.memberNames.length - a.memberNames.length);
}
